/**
 * File: diagramService.ts
 * Description: Service class for rendering diagrams via backend
 * Author: GitHub Copilot
 * Created: 2026-02-03
 * Last Modified: 2026-03-04
 */

import { apiClient } from './apiClient';

const DIAGRAM_RENDER_ENDPOINT = '/api/diagrams/render';
const MERMAID_FIX_ENDPOINT = '/api/mermaid/fix';
const DEFAULT_DIAGRAM_TYPE = 'structurizr';
const ERROR_INVALID_RESPONSE = 'Invalid diagram render response';

export interface DiagramRenderRequest {
  dsl: string;
  diagram_type: string;
  view_key: string | null;
}

export interface DiagramRenderResponse {
  svg_data_url: string;
}

export interface MermaidFixRequest {
  code: string;
  error: string;
  /** Architecture section key used to apply agent-specific Mermaid guidelines. */
  section?: string;
}

export interface MermaidFixResponse {
  fixed_code: string;
}

export class DiagramService {
  /**
   * Render a diagram via the backend service
   * @param dslContent - The Structurizr DSL content
   * @param diagramType - Type of diagram
   * @param viewKey - Optional view key for specific C4 view rendering
   * @return Promise with the SVG data URL
   * @throws Error on request or response failure
   */
  public async renderDiagram(
    dslContent: string,
    diagramType: string = DEFAULT_DIAGRAM_TYPE,
    viewKey?: string,
  ): Promise<string> {
    const requestPayload: DiagramRenderRequest = {
      dsl: dslContent,
      diagram_type: diagramType,
      view_key: viewKey ?? null,
    };

    const response = await apiClient.post<DiagramRenderResponse>(
      DIAGRAM_RENDER_ENDPOINT,
      requestPayload,
    );

    if (!response?.svg_data_url) {
      throw new Error(ERROR_INVALID_RESPONSE);
    }

    return response.svg_data_url;
  }

  /**
   * Ask Archie (LLM) to fix a broken Mermaid diagram.
   *
   * @param code - The raw Mermaid source that failed to render.
   * @param error - The error message returned by the Mermaid renderer.
   * @returns The corrected Mermaid source.
   * @throws ApiError on network or server failures.
   */
  public async fixMermaidDiagram(
    code: string,
    error: string,
    section?: string,
  ): Promise<string> {
    const response = await apiClient.post<MermaidFixResponse>(
      MERMAID_FIX_ENDPOINT,
      { code, error, section } satisfies MermaidFixRequest,
    );
    return response.fixed_code;
  }
}

export const diagramService = new DiagramService();
export { DEFAULT_DIAGRAM_TYPE };
